import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar } from './ui/calendar';
import { Search, MapPin, Calendar as CalendarIcon, Users, Minus, Plus } from 'lucide-react';
import { format } from 'date-fns';

const SearchBar = ({ variant = 'hero', onSearch }) => {
  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState({ adults: 2, children: 0, infants: 0 });
  const [showGuestPopover, setShowGuestPopover] = useState(false);

  const handleSearch = () => {
    if (onSearch) {
      const totalGuests = guests.adults + guests.children + guests.infants;
      onSearch({
        location: location.trim(),
        checkIn,
        checkOut,
        guests: totalGuests
      });
    }
  };

  const updateGuests = (type, operation) => {
    setGuests(prev => ({
      ...prev,
      [type]: operation === 'add' ? prev[type] + 1 : Math.max(0, prev[type] - 1)
    }));
  };

  const totalGuests = guests.adults + guests.children + guests.infants;

  if (variant === 'compact') {
    return (
      <div className="flex items-center space-x-2 border rounded-full shadow-md hover:shadow-lg transition-shadow bg-white px-4 py-2 cursor-pointer"
           onClick={() => navigate('/')}>
        <Search className="h-4 w-4" />
        <div className="flex items-center space-x-3 text-sm">
          <span className="font-semibold">Anywhere</span>
          <span className="text-gray-300">|</span>
          <span className="font-semibold">Any week</span>
          <span className="text-gray-300">|</span>
          <span className="text-gray-500">Add guests</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="bg-white rounded-full shadow-xl border border-gray-200 p-2">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
          {/* Location */}
          <div className="relative group">
            <div className="px-6 py-3 hover:bg-gray-50 rounded-full cursor-pointer transition-colors">
              <label className="block text-xs font-semibold mb-1">Where</label>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search destinations"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="border-0 p-0 h-auto text-sm focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-gray-400"
                />
              </div>
            </div>
          </div>

          {/* Check In */}
          <div className="relative group border-l border-gray-200">
            <Popover>
              <PopoverTrigger asChild>
                <div className="px-6 py-3 hover:bg-gray-50 rounded-full cursor-pointer transition-colors">
                  <label className="block text-xs font-semibold mb-1">Check in</label>
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {checkIn ? format(checkIn, 'MMM dd') : 'Add dates'}
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkIn}
                  onSelect={setCheckIn}
                  initialFocus
                  disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Check Out */}
          <div className="relative group border-l border-gray-200">
            <Popover>
              <PopoverTrigger asChild>
                <div className="px-6 py-3 hover:bg-gray-50 rounded-full cursor-pointer transition-colors">
                  <label className="block text-xs font-semibold mb-1">Check out</label>
                  <div className="flex items-center space-x-2">
                    <CalendarIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-500">
                      {checkOut ? format(checkOut, 'MMM dd') : 'Add dates'}
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={checkOut}
                  onSelect={setCheckOut}
                  initialFocus
                  disabled={(date) => {
                    const today = new Date(new Date().setHours(0, 0, 0, 0));
                    if (date < today) return true;
                    if (checkIn && date <= checkIn) return true;
                    return false;
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Guests */}
          <div className="relative group border-l border-gray-200 flex items-center">
            <Popover open={showGuestPopover} onOpenChange={setShowGuestPopover}>
              <PopoverTrigger asChild>
                <div className="pl-6 pr-4 py-3 hover:bg-gray-50 rounded-full cursor-pointer transition-colors flex-1 min-w-0">
                  <label className="block text-xs font-semibold mb-1">Guests</label>
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-sm text-gray-500 truncate">
                      {totalGuests > 0 ? `${totalGuests} guest${totalGuests > 1 ? 's' : ''}` : 'Add guests'}
                    </span>
                  </div>
                </div>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-4">
                  {/* Adults */}
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <div className="font-semibold">Adults</div>
                      <div className="text-sm text-gray-500">Ages 13 or above</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('adults', 'subtract')}
                        disabled={guests.adults <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center">{guests.adults}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('adults', 'add')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Children */}
                  <div className="flex items-center justify-between py-3 border-b">
                    <div>
                      <div className="font-semibold">Children</div>
                      <div className="text-sm text-gray-500">Ages 2-12</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('children', 'subtract')}
                        disabled={guests.children <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center">{guests.children}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('children', 'add')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Infants */}
                  <div className="flex items-center justify-between py-3">
                    <div>
                      <div className="font-semibold">Infants</div>
                      <div className="text-sm text-gray-500">Under 2</div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('infants', 'subtract')}
                        disabled={guests.infants <= 0}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center">{guests.infants}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateGuests('infants', 'add')}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            
            {/* Search Button - Separate from Guests Popover */}
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSearch();
              }}
              className="ml-2 mr-2 rounded-full h-12 w-12 p-0 bg-[#7CB342] hover:bg-[#6da036] text-white flex-shrink-0"
              data-testid="search-button"
            >
              <Search className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;